import React, { useEffect, useMemo, useRef, useState } from 'react';
import Topbar from '../components/Topbar';
import Leftbar from '../components/Leftbar';
import { Link } from 'react-router-dom';
import {
	addService,
	deleteService,
	displayError,
	displaySuccess,
	getServicesList,
	updateService,
} from '../Utils/utils';
import { toggleLoader } from '../actions/loader';
import { connect } from 'react-redux';
import { Modal } from 'react-bootstrap';
import '../Gynac/style.css';
import './style.css';

const AVATAR_PALETTE = [
	'#c22627', '#5a4cdb', '#d6336c', '#2b6cb0', '#e8590c',
	'#0ca678', '#7048e8', '#1098ad', '#f08c00', '#37b24d',
];

const colorForName = (name = '') => {
	let hash = 0;
	for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
	return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const initialsOf = (name = '') => {
	const parts = name.trim().split(/\s+/);
	if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
	if (parts[0]) return parts[0].substring(0, 2).toUpperCase();
	return '?';
};

const ServicesList = (props) => {
	const fileInputRef = useRef(null);
	const [services, setServices] = useState([]);
	const [detailModal, setDetailModal] = useState(null);
	const [search, setSearch] = useState('');

	const [showAdd, setShowAdd] = useState(false);
	const [editId, setEditId] = useState(null);
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [file, setFile] = useState(null);
	const [fileName, setFileName] = useState('');

	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deleting, setDeleting] = useState(false);

	const getList = async () => {
		props.toggleLoader(true);
		const response = await getServicesList();
		setServices(response?.data || []);
		props.toggleLoader(false);
	};

	useEffect(() => {
		getList();
	}, []);

	const resetForm = () => {
		setName('');
		setDescription('');
		setFile(null);
		setFileName('');
	};

	const handleCloseAdd = () => {
		setShowAdd(false);
		setEditId(null);
		resetForm();
	};

	const openEdit = (service) => {
		setEditId(service?._id);
		setName(service?.name || '');
		setDescription(service?.description || '');
		setFile(null);
		setFileName('');
		setShowAdd(true);
	};

	const handleFileChange = (e) => {
		if (e.target.files && e.target.files.length > 0) {
			const f = e.target.files[0];
			setFile(f);
			setFileName(f.name);
		}
	};

	const submitService = async () => {
		if (!name) {
			displayError({ response: { status: 400, data: { message: 'Name is required' } } });
			return;
		}

		props.toggleLoader(true);
		try {
			const data = new FormData();
			data.append('name', name);
			data.append('description', description);
			if (file) data.append('image', file);

			const res = editId
				? await updateService(editId, data)
				: await addService(data);
			props.toggleLoader(false);
			displaySuccess(
				res?.data?.message ||
				(editId ? 'Service updated successfully.' : 'Service added successfully.')
			);
			handleCloseAdd();
			getList();
		} catch (error) {
			props.toggleLoader(false);
			displayError(error);
		}
	};

	const removeService = async () => {
		if (!deleteTarget?._id) return;
		setDeleting(true);
		props.toggleLoader(true);
		try {
			const res = await deleteService(deleteTarget._id);
			props.toggleLoader(false);
			displaySuccess(res?.data?.message || 'Service deleted successfully.');
			setDeleteTarget(null);
			getList();
		} catch (error) {
			props.toggleLoader(false);
			displayError(error);
		} finally {
			setDeleting(false);
		}
	};

	const filteredServices = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return services || [];
		return (services || []).filter((s) => {
			const n = (s?.name || '').toLowerCase();
			const d = (s?.description || '').toLowerCase();
			return n.includes(q) || d.includes(q);
		});
	}, [services, search]);

	return (
		<div className="g-sidenav-show bg-gray-100">
			<div id="wrapper">
				<Leftbar />

				<main className="main-content border-radius-lg">
					<Topbar />
					<div className="header bg-primary pb-6">
						<div className="container-fluid">
							<div className="header-body">
								<div className="row align-items-center py-4">
									<div className="col-lg-6 col-7">
										<nav aria-label="breadcrumb" className="d-none d-md-inline-block ml-md-4">
											<ol className="breadcrumb breadcrumb-links breadcrumb-dark">
												<li className="breadcrumb-item">
													<Link to="/dashboard"><i className="fas fa-home" /></Link>
												</li>
												<li className="breadcrumb-item">
													<a href="#">Services</a>
												</li>
											</ol>
										</nav>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div className="container-fluid mt--6"></div>
					<div className="container-fluid">
						<div className="card gynac-card">
							<div className="gynac-card-header">
								<h3 className="gynac-title">
									Services Listing
									<span className="gynac-count-chip">
										{filteredServices.length}{search ? ` of ${services.length}` : ''}
									</span>
								</h3>

								<div className="gynac-toolbar">
									<div className="gynac-search">
										<i className="fas fa-search search-icon" />
										<input
											type="text"
											placeholder="Search service name or description"
											value={search}
											onChange={(e) => setSearch(e.target.value)}
										/>
									</div>
									<button className="btn gynac-add-btn" onClick={() => setShowAdd(true)}>
										<i className="fas fa-plus" style={{ marginRight: 6 }} />
										Add Service
									</button>
								</div>
							</div>

							<div className="table-responsive">
								<table className="gynac-table services-table">
									<colgroup>
										<col className="services-col-service" />
										<col className="services-col-description" />
										<col className="services-col-action" />
									</colgroup>
									<thead>
										<tr>
											<th>Service</th>
											<th>Description</th>
											<th style={{ textAlign: 'center' }}>Action</th>
										</tr>
									</thead>
									<tbody>
										{filteredServices.map((service) => {
											const svcName = service?.name || '—';
											const initials = initialsOf(svcName);
											const avatarColor = colorForName(svcName);
											const desc = service?.description || '';
											const isLongDesc = desc.length > 120;

											return (
												<tr key={service?._id}>
													<td data-label="Service">
														<div className="doctor-cell">
															{service?.image ? (
																<img
																	src={service.image}
																	alt={svcName}
																	className="doctor-avatar"
																/>
															) : (
																<span
																	className="doctor-avatar-fallback"
																	style={{ background: avatarColor }}
																>
																	{initials}
																</span>
															)}
															<div className="doctor-info">
																<span className="doctor-name" title={svcName}>{svcName}</span>
															</div>
														</div>
													</td>

													<td data-label="Description">
														{desc ? (
															<>
																<div className="desc-text">{desc}</div>
																{isLongDesc && (
																	<span
																		className="desc-view-more"
																		onClick={() => setDetailModal({ title: 'Description', text: desc, subtitle: svcName })}
																	>
																		View More
																	</span>
																)}
															</>
														) : (
															<span className="text-muted">—</span>
														)}
													</td>

													<td data-label="Action" style={{ textAlign: 'center' }}>
														<div className="services-action">
															<button
																className="btn-pill btn-approve"
																onClick={() => openEdit(service)}
															>
																<i className="fas fa-pen" style={{ marginRight: 5, fontSize: '0.7rem' }} />
																Edit
															</button>
															<button
																className="btn-pill btn-reject"
																onClick={() => setDeleteTarget(service)}
															>
																<i className="fas fa-trash" style={{ marginRight: 5, fontSize: '0.7rem' }} />
																Delete
															</button>
														</div>
													</td>
												</tr>
											);
										})}

										{!filteredServices.length && (
											<tr>
												<td colSpan={3}>
													<div className="gynac-empty">
														<i className="fas fa-concierge-bell" />
														<div className="gynac-empty-text">No services found</div>
													</div>
												</td>
											</tr>
										)}
									</tbody>
								</table>
							</div>
						</div>
					</div>

					<Modal show={showAdd} onHide={handleCloseAdd} centered size="lg">
						<Modal.Header style={{ borderBottom: '1px solid #e9ecef' }}>
							<div>
								<h5 className="mb-0" style={{ fontWeight: 700 }}>{editId ? 'Edit Service' : 'Add Service'}</h5>
							</div>
							<button
								type="button"
								style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
								onClick={handleCloseAdd}
							>
								&times;
							</button>
						</Modal.Header>
						<Modal.Body style={{ padding: '20px 24px' }}>
							<div className="row">
								<div className="col-md-12">
									<div className="form-group">
										<label className="form-control-label">Name</label>
										<input
											type="text"
											className="form-control"
											placeholder="Name"
											value={name}
											onChange={(e) => setName(e.target.value)}
										/>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label className="form-control-label">Description</label>
										<textarea
											className="form-control"
											placeholder="Description"
											value={description}
											onChange={(e) => setDescription(e.target.value)}
										></textarea>
									</div>
								</div>
								<div className="col-md-12">
									<div className="form-group">
										<label className="form-control-label me-2">Upload Image</label>
										<button
											className="btn pull-right"
											style={{
												backgroundColor: '#fff',
												border: '1px solid rgb(194, 38, 39)',
												color: 'rgb(194, 38, 39)',
												borderRadius: '40px',
											}}
											onClick={() => fileInputRef.current.click()}
										>
											Browse
										</button>
										<input
											type="file"
											ref={fileInputRef}
											accept=".jpg,.jpeg,.png"
											name="image"
											style={{ display: 'none' }}
											onChange={handleFileChange}
										/>
										{fileName && (
											<p style={{ marginTop: '5px' }}>Selected: {fileName}</p>
										)}
									</div>
								</div>
							</div>
						</Modal.Body>
						<Modal.Footer style={{ borderTop: '1px solid #e9ecef' }}>
							<button
								className="btn"
								style={{
									background: '#fff', border: '1px solid #C22627',
									color: '#C22627', borderRadius: '40px', fontWeight: 600
								}}
								onClick={handleCloseAdd}
							>
								Cancel
							</button>
							<button className="btn btn-red" onClick={submitService}>
								<i className="far fa-paper-plane" /> Submit
							</button>
						</Modal.Footer>
					</Modal>

					<Modal
						show={!!deleteTarget}
						onHide={() => !deleting && setDeleteTarget(null)}
						centered
						backdrop="static"
					>
						<Modal.Header style={{ borderBottom: '1px solid #e9ecef' }}>
							<div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
								<span
									style={{
										width: 40, height: 40, borderRadius: '50%',
										background: '#fdebec', color: '#c22627',
										display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
										fontSize: 18
									}}
								>
									<i className="fas fa-exclamation-triangle" />
								</span>
								<h5 className="mb-0" style={{ fontWeight: 700 }}>Delete service?</h5>
							</div>
							<button
								type="button"
								style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
								onClick={() => !deleting && setDeleteTarget(null)}
								disabled={deleting}
							>
								&times;
							</button>
						</Modal.Header>
						<Modal.Body style={{ padding: '20px 24px', fontSize: '0.95rem', color: '#333' }}>
							Are you sure you want to delete{' '}
							<strong style={{ color: '#1f2540' }}>
								{deleteTarget?.name || 'this service'}
							</strong>
							? This action cannot be undone.
						</Modal.Body>
						<Modal.Footer style={{ borderTop: '1px solid #e9ecef' }}>
							<button
								className="btn"
								style={{
									background: '#fff', border: '1px solid #d0d4e0',
									color: '#6b7088', borderRadius: 999, fontWeight: 600,
									padding: '6px 18px'
								}}
								onClick={() => setDeleteTarget(null)}
								disabled={deleting}
							>
								Cancel
							</button>
							<button
								className="btn"
								style={{
									background: '#c22627', color: '#fff',
									border: '1px solid #c22627', borderRadius: 999, fontWeight: 600,
									padding: '6px 18px',
									opacity: deleting ? 0.7 : 1,
									cursor: deleting ? 'not-allowed' : 'pointer'
								}}
								onClick={removeService}
								disabled={deleting}
							>
								<i className="fas fa-trash" style={{ marginRight: 6, fontSize: '0.78rem' }} />
								{deleting ? 'Deleting…' : 'Delete'}
							</button>
						</Modal.Footer>
					</Modal>

					<Modal show={!!detailModal} onHide={() => setDetailModal(null)} centered>
						<Modal.Header style={{ borderBottom: '1px solid #e9ecef' }}>
							<div>
								<h5 className="mb-0" style={{ fontWeight: 700 }}>{detailModal?.title}</h5>
								{detailModal?.subtitle && <small className="text-muted">{detailModal.subtitle}</small>}
							</div>
							<button
								type="button"
								style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}
								onClick={() => setDetailModal(null)}
							>
								&times;
							</button>
						</Modal.Header>
						<Modal.Body style={{ padding: '20px 24px', fontSize: '0.95rem', lineHeight: 1.7, color: '#333', maxHeight: '70vh', overflowY: 'auto' }}>
							{detailModal?.text}
						</Modal.Body>
					</Modal>
				</main>
			</div>
		</div>
	);
};

export default connect(null, { toggleLoader })(ServicesList);
